import { useState, useMemo, type CSSProperties } from 'react';
import { Title } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  DragOverlay,
  useDroppable
} from '@dnd-kit/core';
import { FileText } from 'tabler-icons-react';
import '../pages/KanbanColumn.css';
import { Opportunity, OpportunityForm, STAGES, stageColors, stageIcons } from './OpportunityPageTypes';
import { handleStageChange } from './OpportunityPageUtilities';
import { OpportunityCard, DraggableOpportunityCard } from './OpportunityPageCards';
import { getStageLabel } from '../i18n/stageLabels';
import { useIsMobileLayout } from '../utility/mobilePageLayout';

function KanbanColumnOpportunity({
  stage,
  stageLabel,
  opportunities,
  onClick,
}: {
  stage: string;
  stageLabel: string;
  opportunities: Opportunity[];
  onClick: (opp: Opportunity) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: stage });
  const isMobile = useIsMobileLayout();

  const columnStyle: CSSProperties = {
    backgroundColor: isOver ? '#fafafa' : 'rgb(239,239,239)',
    padding: '16px',
    borderTop: '3px solid #888',
    borderRadius: '8px',
    flex: 1,
    minHeight: isMobile ? undefined : '730px',
    maxWidth: '350px',
    overflowY: isMobile ? 'visible' : 'auto',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    margin: '0 8px',
    display: 'flex',
    flexDirection: 'column'
  };

  return (
    <div ref={setNodeRef} style={columnStyle}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #eee',
          paddingBottom: '16px'
        }}
      >
        <Title order={5} style={{ textAlign: 'left', margin: 0, fontWeight: 600 }}>
          {stageLabel} ({opportunities.length})
        </Title>
      </div>
      <div
        className="cardsContainer"
        style={isMobile ? undefined : { overflowY: 'auto', maxHeight: '700px' }}
      >
        {opportunities.map((opp) => (
          <DraggableOpportunityCard key={opp.id} opportunity={opp} onClick={() => onClick(opp)} />
        ))}
      </div>
    </div>
  );
}

export function OpportunityKanbanView({
  opportunities,
  updateOpportunity,
  onClick
}: {
  opportunities: Opportunity[];
  updateOpportunity: (opp: OpportunityForm, id: string) => void;
  onClick: (opp: Opportunity) => void;
}) {
  const { t, i18n } = useTranslation();
  const [activeOpportunity, setActiveOpportunity] = useState<Opportunity | null>(null);

  const stageLabels = useMemo(
    () => Object.fromEntries(STAGES.map((stage) => [stage, getStageLabel(stage, t)])),
    [t, i18n.language],
  );

  const handleDragStart = (event: any) => {
    const id = event.active.id;
    const parts = id.split('/');
    const oppId = parts[0];
    const opp = opportunities.find((a) => a.id === oppId) || null;
    setActiveOpportunity(opp);
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (over && active.id) {
      const newStage = over.id as 'Value Proposition' | 'Quotation' | 'Demonstration' | 'Negociation' | 'Closed';
      const oppId = active.id.split('/')[0];
      const opp = opportunities.find((op) => op.id === oppId);

      if (opp) {
        const currentStage = opp.stages.filter(st => st.finished_at === null)[0] || opp.stages[0];
        if (newStage === 'Closed') {
          if (!opp.noSalesMade && !opp.products.some(p => p.sold === true)) {
            alert(t('opportunity.alerts.cannotCloseWithoutSales'));
            setActiveOpportunity(null);
            return;
          }
        }
        if (currentStage.name === newStage) {
          setActiveOpportunity(null);
          return;
        }
        if (currentStage.name === 'Closed') {
          alert(t('opportunity.alerts.alreadyClosing'));
          setActiveOpportunity(null);
          return;
        }
        handleStageChange(opp, newStage, updateOpportunity);
      }
    }
    setActiveOpportunity(null);
  };

  const grouped = useMemo(() => {
    const groups: Record<string, Opportunity[]> = {};
    STAGES.forEach((stage) => {
      groups[stage] = [];
    });
    opportunities.forEach((opp) => {
      const currentStage = opp.stages.filter(st => st.finished_at === null)[0] || opp.stages[0];
      if (groups[currentStage.name]) {
        groups[currentStage.name].push(opp);
      }
    });
    return groups;
  }, [opportunities]);

  return (
    <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div style={{ display: 'flex', justifyContent: 'flex-start', width: 'max-content', minWidth: '100%' }}>
        {STAGES.map((stage) => (
          <KanbanColumnOpportunity
            key={stage}
            stage={stage}
            stageLabel={stageLabels[stage]}
            opportunities={grouped[stage]}
            onClick={onClick}
          />
        ))}
      </div>
      <DragOverlay>
        {activeOpportunity ? (
          <OpportunityCard
            opportunity={activeOpportunity}
            record={activeOpportunity.stages[activeOpportunity.stages.length - 1]}
            color={stageColors[activeOpportunity.stages[activeOpportunity.stages.length - 1].name] || '#000'}
            icon={stageIcons[activeOpportunity.stages[activeOpportunity.stages.length - 1].name] || <FileText size={18} />}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
